import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { usePageMetadata } from '../../hooks/usePageMetadata';
import {
    Package,
    CheckCircle,
    Clock,
    XCircle,
    TrendingUp,
    Plus,
    AlertCircle,
} from 'lucide-react';
import { useGetDealerDashboardStatsQuery } from '../../store/slices/apiSlice';
import toast from 'react-hot-toast';

const DealerDashboard: React.FC = () => {
    const { user, token } = useSelector((state: RootState) => state.auth);
    const navigate = useNavigate();
    
    usePageMetadata('dashboard');

    const { data: statsData, isLoading, error, refetch } = useGetDealerDashboardStatsQuery(undefined, {
        skip: !token,
    });

    const stats = statsData?.data || {
        total_listings: 0,
        active_listings: 0,
        pending_listings: 0,
        sold_listings: 0,
        rejected_listings: 0,
    };

    if (!user) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-red-600 text-sm">Please log in to view your dashboard</div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        const errorMessage = (error as any)?.data?.message || 'Failed to load dashboard stats';
        const isPendingVerification = errorMessage.includes('pending verification');
        
        return (
            <div className="space-y-4 max-w-7xl mx-auto px-4 py-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="w-6 h-6 text-yellow-600 mt-0.5" />
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-yellow-900 mb-2">Account Verification Required</h3>
                            <p className="text-sm text-yellow-700 mb-4">
                                {errorMessage}
                            </p>
                            <Button
                                onClick={() => navigate('/dealer/profile')}
                                className="bg-yellow-600 hover:bg-yellow-700 text-white"
                            >
                                Go to Profile
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const statCards = [
        {
            title: 'Total Listings',
            value: stats.total_listings,
            icon: Package,
            color: 'bg-blue-500',
            bgColor: 'bg-blue-50',
            textColor: 'text-blue-600',
        },
        {
            title: 'Active Listings',
            value: stats.active_listings,
            icon: CheckCircle,
            color: 'bg-green-500',
            bgColor: 'bg-green-50',
            textColor: 'text-green-600',
        },
        {
            title: 'Pending Approval',
            value: stats.pending_listings,
            icon: Clock,
            color: 'bg-yellow-500',
            bgColor: 'bg-yellow-50',
            textColor: 'text-yellow-600',
        },
        {
            title: 'Sold Listings',
            value: stats.sold_listings,
            icon: TrendingUp,
            color: 'bg-purple-500',
            bgColor: 'bg-purple-50',
            textColor: 'text-purple-600',
        },
        {
            title: 'Rejected Listings',
            value: stats.rejected_listings,
            icon: XCircle,
            color: 'bg-red-500',
            bgColor: 'bg-red-50',
            textColor: 'text-red-600',
        },
    ];

    return (
        <div className="space-y-6 max-w-7xl mx-auto px-4 py-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dealer Dashboard</h1>
                    <p className="text-sm text-gray-600 mt-1">Overview of your listings and account</p>
                </div>
                <Button
                    onClick={() => navigate('/dealer/listings/create')}
                    className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />
                    <span>Create New Listing</span>
                </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {statCards.map((stat, index) => {
                    const IconComponent = stat.icon;
                    return (
                        <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                            <div className="flex items-center justify-between mb-4">
                                <div className={`w-12 h-12 ${stat.bgColor} rounded-lg flex items-center justify-center`}>
                                    <IconComponent className={`w-6 h-6 ${stat.textColor}`} />
                                </div>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                            </div>
                        </Card>
                    );
                })}
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                    <div className="space-y-3">
                        <Button
                            variant="outline"
                            onClick={() => navigate('/dealer/listings')}
                            className="w-full justify-start"
                        >
                            <Package className="w-4 h-4 mr-2" />
                            View All Listings
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => navigate('/dealer/listings?approval_status=pending')}
                            className="w-full justify-start"
                        >
                            <Clock className="w-4 h-4 mr-2" />
                            Pending Approval
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => navigate('/dealer/listings/sold')}
                            className="w-full justify-start"
                        >
                            <TrendingUp className="w-4 h-4 mr-2" />
                            Sold Listings
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => navigate('/dealer/profile')}
                            className="w-full justify-start"
                        >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Manage Profile
                        </Button>
                    </div>
                </Card>

                <Card className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Status</h3>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm text-gray-600">Total Listings</span>
                            <span className="text-sm font-semibold text-gray-900">{stats.total_listings}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm text-gray-600">Active Listings</span>
                            <span className="text-sm font-semibold text-green-600">{stats.active_listings}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm text-gray-600">Pending Approval</span>
                            <span className="text-sm font-semibold text-yellow-600">{stats.pending_listings}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm text-gray-600">Sold</span>
                            <span className="text-sm font-semibold text-purple-600">{stats.sold_listings}</span>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default DealerDashboard;
